"use client";

import { useInViewStore } from "@/store/inViewStore";
import Link from "next/link";
import {
  KeyboardEvent,
  MouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { navLinks } from "@/constants/navigation";
import { AnimatedThemeToggler } from "@/ui/theme/animated-theme-toggler"
import { cn } from "@/utils";

type IndicatorState = {
  width: number;
  offset: number;
};

function NaviButton({
  navItem,
  isActive,
  onFocus,
}: {
  navItem: {
    name: string;
    link: string;
    icon: ({ title }: { title: string }) => JSX.Element;
  };
  isActive: boolean;
  onFocus?: () => void;
}) {
  return (
    <li className="relative flex-1 sm:flex-none">
      <Link
        aria-label={navItem.name}
        role="button"
        href={navItem.link}
        onFocus={onFocus}
        className={cn(
          "relative flex items-center justify-center gap-1 sm:gap-2 rounded-full px-2.5 sm:px-4 md:px-5 py-2 sm:py-2.5 md:py-3 text-[0.65rem] sm:text-[0.7rem] font-semibold uppercase tracking-[0.12em] transition-colors duration-200 whitespace-nowrap w-full sm:w-auto",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ffe36c] focus-visible:ring-offset-2 focus-visible:ring-offset-transparent",
          isActive ? "text-[#05060e]" : "text-[var(--text-muted)]",
        )}
      >
        <span className="flex h-5 w-5 sm:h-6 sm:w-6 items-center justify-center text-current flex-shrink-0">
          {navItem.icon && <navItem.icon title={navItem.name} />}
        </span>
        <span className="text-current hidden sm:inline">{navItem.name}</span>
      </Link>
    </li>
  );
}

export default function Navi() {
  const [mounted, setMounted] = useState(false);
  const [indicatorState, setIndicatorState] = useState<IndicatorState>({
    width: 0,
    offset: 0,
  });
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);
  const rafRef = useRef<number>();
  const sectionsInView = useInViewStore((state) => state.sectionsInView);

  useEffect(() => {
    setMounted(true);
  }, []);

  const setIndicatorPosition = (
    anchor?: HTMLAnchorElement | null,
    parent?: HTMLElement | null,
  ) => {
    if (!anchor || !listRef.current) return;

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    rafRef.current = requestAnimationFrame(() => {
      const anchorRect = anchor.getBoundingClientRect();
      const referenceRect = listRef.current?.getBoundingClientRect();
      const offsetLeft =
        referenceRect && anchorRect
          ? anchorRect.left - referenceRect.left
          : parent?.offsetLeft ?? 0;

      setIndicatorState({
        width: anchorRect.width,
        offset: offsetLeft,
      });
    });
  };

  const updateIndicatorForIndex = (index: number) => {
    if (!listRef.current) return;
    const anchors = listRef.current.querySelectorAll("a");
    const anchor = anchors[index] as HTMLAnchorElement | undefined;
    if (anchor) {
      setIndicatorPosition(anchor);
    }
  };

  useEffect(() => {
    const allAnchors = Array.from(listRef.current?.querySelectorAll("a") ?? []);
    if (allAnchors.length === 0) {
      return;
    }

    let maxRatio = -1;
    let candidateIndex = -1;

    navLinks.forEach((navLink, navIndex) => {
      const sectionId = navLink.link.replace("#", "");
      const section = sectionsInView.find((s) => s.id === sectionId);

      if (section?.inView && section.intersectionRatio > maxRatio) {
        maxRatio = section.intersectionRatio;
        candidateIndex = navIndex;
      }
    });

    if (candidateIndex === -1 && typeof window !== "undefined") {
      const viewportCenter = window.innerHeight / 2;
      let minDistance = Infinity;

      navLinks.forEach((navLink, navIndex) => {
        const sectionId = navLink.link.replace("#", "");
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          const elementCenter = rect.top + rect.height / 2;
          const distance = Math.abs(elementCenter - viewportCenter);

          if (distance < minDistance) {
            minDistance = distance;
            candidateIndex = navIndex;
          }
        }
      });
    }

    if (candidateIndex === -1) {
      return;
    }

    setActiveIndex(candidateIndex);
    updateIndicatorForIndex(candidateIndex);
  }, [sectionsInView]);

  useEffect(() => {
    const anchor = listRef.current?.querySelector("a");
    setIndicatorPosition(anchor ?? null, anchor?.parentElement ?? null);
  }, []);

  useEffect(() => {
    updateIndicatorForIndex(activeIndex);
  }, [activeIndex]);

  useEffect(() => {
    const handleResize = () => {
      updateIndicatorForIndex(activeIndex);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [activeIndex]);

  useEffect(() => {
    const anchor = listRef.current?.querySelectorAll("a")?.[
      activeIndex
    ] as HTMLAnchorElement | undefined;
    if (anchor) {
      setIndicatorPosition(anchor);
    }
  }, [activeIndex]);

  const handleNavEvent = (
    e: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>,
  ) => {
    let anchor: HTMLAnchorElement | null = null;

    if (
      e.target instanceof HTMLAnchorElement ||
      e.target instanceof SVGElement
    ) {
      anchor = e.target.closest("a");
    }

    if (anchor != null) {
      // biome-ignore lint/style/noNonNullAssertion: <explanation>
      const allAnchors = Array.from(listRef.current?.querySelectorAll("a")!);
      const index = allAnchors.indexOf(anchor);
      if (index !== -1) {
        setActiveIndex(index);
        updateIndicatorForIndex(index);
      } else {
        setIndicatorPosition(anchor);
      }
    }
  };

  const indicatorStyles = useMemo(() => {
    const width = indicatorState.width || 90;
    const offset = indicatorState.offset || 0;

    return {
      width,
      transform: `translateX(${offset}px)`,
      opacity: indicatorState.width ? 0.95 : 0,
    };
  }, [indicatorState]);

  const navContent = (
    <div className="pointer-events-none fixed left-0 top-0 z-[5000] flex w-full justify-center px-3 sm:px-4 pt-3 sm:pt-4 md:px-6">
      <div className="w-full max-w-[calc(100vw-1.5rem)] pointer-events-auto text-[11px] sm:text-[12px] md:text-[13px] sm:w-auto sm:max-w-none">
        <nav
          className="mx-auto w-full sm:w-auto rounded-full border px-2 sm:px-2 py-1 sm:py-1"
          onClick={handleNavEvent}
          onKeyUp={handleNavEvent}
          onKeyDown={handleNavEvent}
          style={{
            borderColor: "var(--glass-border)",
            background: "rgba(var(--ink-rgb), 0.85)",
            boxShadow:
              "0 25px 45px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.08)",
            backdropFilter: "blur(16px)",
          }}
        >
          <ul className="relative m-0 flex list-none items-center justify-between sm:justify-start gap-1 sm:gap-1 p-0 w-full sm:w-auto" ref={listRef}>
            <li className="pointer-events-none absolute left-0 top-0 h-full w-full">
              <div
                className="absolute left-0 top-1 h-[calc(100%-8px)] rounded-full shadow-[0_12px_30px_rgba(0,0,0,0.35),inset_0_-1px_0_rgba(255,255,255,0.4)] transition-[transform,width] duration-[350ms] ease-[cubic-bezier(0.4,0,0.2,1)]"
                style={{
                  background: "linear-gradient(120deg, #ffe36c, #c58bff)",
                  ...indicatorStyles,
                }}
                aria-hidden="true"
              />
            </li>
            {navLinks.map((navItem, index) => (
              <NaviButton
                key={navItem.name}
                navItem={navItem}
                isActive={index === activeIndex}
                onFocus={() => {
                  updateIndicatorForIndex(index);
                }}
              />
            ))}
            <li className="flex items-center justify-center border border-gray-400/50 rounded-full ml-1 sm:ml-4 p-1.5 sm:p-2 flex-shrink-0 sm:flex-none">
              <AnimatedThemeToggler />
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );

  if (!mounted) return null;

  return createPortal(navContent, document.body);
}
